import React from 'react';

enum ThumbnailSource {
  CHECKING = 'checking',
  PREVIEW = 'preview',
  FIRST_FRAME = 'firstFrame'
}

type UseVideoPosterResult = {
  poster: string | undefined;
  preload: 'none' | 'auto';
  showPlaceholder: boolean;
  handleLoadedData: () => void;
};

export const useVideoPoster = (previewUrl?: string): UseVideoPosterResult => {
  const [source, setSource] = React.useState<ThumbnailSource>(
    previewUrl ? ThumbnailSource.CHECKING : ThumbnailSource.FIRST_FRAME
  );
  const [frameLoaded, setFrameLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!previewUrl) {
      setSource(ThumbnailSource.FIRST_FRAME);
      return;
    }

    const img = new Image();
    img.onload = () => setSource(ThumbnailSource.PREVIEW);
    img.onerror = () => setSource(ThumbnailSource.FIRST_FRAME);
    img.src = previewUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [previewUrl]);

  const handleLoadedData = React.useCallback(() => {
    setFrameLoaded(true);
  }, []);

  const isPreviewReady = source === ThumbnailSource.PREVIEW;

  return {
    poster: isPreviewReady ? previewUrl : undefined,
    preload: isPreviewReady ? 'none' : 'auto',
    showPlaceholder: !isPreviewReady && !frameLoaded,
    handleLoadedData
  };
};
